import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Download, Share, X, PlusSquare } from "lucide-react";

const InstallCtx = createContext({ canInstall: false, installed: false, install: () => {} });
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

export function InstallProvider({ children }) {
  const [evt, setEvt] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setEvt(e); };
    const onInstalled = () => { setInstalled(true); setOpen(false); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("appinstalled", onInstalled); };
  }, []);

  useEffect(() => {
    if (installed) return;
    const at = Number(localStorage.getItem("csm_install_dismissed_at") || 0);
    // First visit: after 1 minute. After a dismissal: again once an hour has passed.
    const wait = at ? Math.max(at + 3600e3 - Date.now(), 60000) : 60000;
    const t = setTimeout(() => setOpen(true), wait);
    return () => clearTimeout(t);
  }, [installed, open]);

  const install = useCallback(async () => {
    if (evt) {
      evt.prompt();
      const { outcome } = await evt.userChoice;
      setEvt(null);
      if (outcome === "accepted") setInstalled(true);
      setOpen(false);
    } else setOpen(true);
  }, [evt]);

  const dismiss = () => { localStorage.setItem("csm_install_dismissed_at", String(Date.now())); setOpen(false); };

  return (
    <InstallCtx.Provider value={{ canInstall: !installed, installed, install, openPopup: () => setOpen(true) }}>
      {children}
      {open && !installed && <InstallPopup native={!!evt} onInstall={install} onClose={dismiss} />}
    </InstallCtx.Provider>
  );
}

export const useInstall = () => useContext(InstallCtx);

function InstallPopup({ native, onInstall, onClose }) {
  const [help, setHelp] = useState(false);
  return (
    <div className="fixed inset-x-3 bottom-20 lg:bottom-5 sm:left-auto sm:right-5 sm:w-[360px] z-[60] lux-card !bg-[#0d0f14]/95 backdrop-blur-xl border-[#D4AF37]/40 p-4 shadow-2xl rise" data-testid="install-popup">
      <button onClick={onClose} className="absolute top-3 right-3 text-gray-500 hover:text-white" aria-label="Close" data-testid="install-popup-close"><X className="w-4 h-4" /></button>
      <div className="flex items-center gap-3 mb-3">
        <img src="/icon-192.png" alt="" className="w-12 h-12 rounded-xl border gold-line" />
        <div><p className="font-semibold text-sm">Install CarSaleMarket</p><p className="text-xs text-gray-400">Add to your home screen for one-tap access</p></div>
      </div>
      <button onClick={native ? onInstall : () => setHelp(true)} className="btn-gold w-full text-sm" data-testid="install-popup-btn"><Download className="w-4 h-4" />Install App</button>
      {!native && help && (isIOS()
        ? <p className="text-xs text-gray-300 flex flex-wrap items-center gap-1 mt-3" data-testid="install-popup-ios">Tap <Share className="w-3.5 h-3.5 text-[#E5C158]" /> <strong>Share</strong> then <PlusSquare className="w-3.5 h-3.5 text-[#E5C158]" /> <strong>Add to Home Screen</strong>.</p>
        : <p className="text-xs text-gray-300 mt-3" data-testid="install-popup-help">Open your browser menu (⋮) and choose <strong>Install app</strong> or <strong>Add to Home screen</strong>.</p>)}
    </div>
  );
}

export function InstallButton({ className = "", testid = "install-app-btn", compact = false }) {
  const { canInstall, openPopup: install } = useInstall();
  if (!canInstall) return null;
  if (compact) return <button onClick={install} title="Install App" aria-label="Install App" className={`w-9 h-9 rounded-full border border-white/10 hover:border-[#D4AF37]/60 flex items-center justify-center text-gray-200 ${className}`} data-testid={testid}><Download className="w-4 h-4" /></button>;
  return <button onClick={install} className={`btn-outline text-sm !py-2 ${className}`} data-testid={testid}><Download className="w-3.5 h-3.5" />Install App</button>;
}
